/**
 * Showcase scene model (R10 — showcase↔conversation synergy Round A; R12 field-fix — adds 'close';
 * R13 U1 — adds 'end'; R15 T1 — adds 'attract').
 *
 * The showcase panel is an explicit state machine of *scenes* (design doc
 * `showcase-conversation-synergy.md` §3):
 *   - 'attract'      → S0 attract/welcome resting scene (R15 T1): the pre-connect resting state —
 *                      brand welcome + ambient visual + a single "Tap Start talking — Gigi will show
 *                      you around" cue. NO form / category grid / browse pre-connect. This is the
 *                      INITIAL scene and the scene the session RESTS on (fresh load, post-session
 *                      reset via the end scene, disconnect). CLIENT-ONLY — the server never emits it;
 *                      the kiosk leaves it on connect (see `connectScene`).
 *   - 'welcome'      → S1 contact card (name/company + business-card scan-to-fill) — now the first
 *                      IN-CALL beat, not the resting state (R15 T1). Reached on connect from attract.
 *   - 'browse'       → S2 category grid + product cards (the LIVE default surface)
 *   - 'order_review' → S6 selection confirm before place_order / send_spec_sheet
 *   - 'close'        → S7 close card: order recap + keyboard-first email + sent/queued state
 *                      (R12 — arrives on `ui.scene {scene:'close'}` after Confirm/place_order).
 *   - 'end'          → S9 end scene: the style-snapshot reveal + thanks + feedback framing
 *                      (R13 U1 — arrives on `ui.scene {scene:'end'}` after the close card's Done,
 *                      which also emits `close-done`). TERMINAL + sticky (see below).
 *
 * Two drivers, per the design doc:
 *   1. The server emits `ui.scene {scene, payload?}` over RTVI (same channel as ui.listening).
 *   2. Local visitor touch can ALWAYS advance/dismiss — voice never traps the visitor.
 *      A local dismiss of 'welcome' is *sticky*: once the visitor skips ("Just browsing"),
 *      a later server `ui.scene {welcome}` must NOT drag them back to the card. Server scene
 *      changes to *other* scenes (order_review / close) still take effect.
 *
 * R12 STOMP-PROOF (F1/F3 — "spec-sheet screen flashed then reverted"): 'close' is the post-order
 * terminal scene. Two guards protect it:
 *   a) The scene *container* (ShowcaseScene) ignores late `ui.show_products` renders while in
 *      'close' — a stray card push cannot swap the recap for a product grid. (Container-side; this
 *      module owns only the scene value.)
 *   b) `orderClosed` stickiness (below): once we reach 'close', a server `ui.scene {browse}` does
 *      NOT drag us back — the field bug was a queued post-commit browse racing in. Only 'order_review'
 *      (visitor reopening their selection) or a full session reset leaves 'close'. Local touch is a
 *      full peer and may always leave (the "Done" affordance uses a local scene change).
 *
 * R30 V2 — the S7 close-FIELD guard (`closeFieldGuard`, a third arg to `applyServerScene`): while a
 * close-card field is focused with a non-empty draft, NO server scene change leaves 'close'. The
 * dev-117 field session: the visitor was typing her email when a server `ui.scene:'end'` swapped the
 * close card for the style walkthrough — the draft was destroyed and the screen glitched. Unlike
 * guard (b) (which blocks only a post-commit `browse`), this holds against ANY server transition off
 * 'close' — critically the terminal `end` (the "said done" advance) that (b) allows through. It is a
 * CLIENT-LOCAL guard driven by DOM focus + draft state, so it works regardless of server emissions.
 * Local touch (the visitor's own Done/submit) is never blocked — it goes through `applyLocalScene`.
 *
 * This module is pure (no React / no DOM) so the transition rules are unit-testable.
 */

export type SceneName = 'attract' | 'welcome' | 'browse' | 'order_review' | 'close' | 'end';

/**
 * The RTVI scene values the server may emit (voice-capture-contract.md R26; 'close' added R12,
 * 'end' added R13 U1). NOTE (R15 T1): 'attract' is deliberately NOT here — it is a CLIENT-ONLY
 * resting scene the server never drives (the session is pre-connect / has no server). `isSceneName`
 * is used to validate inbound server `ui.scene` values, so keeping 'attract' out means a stray
 * server 'attract' is ignored rather than dragging a live visitor back to the resting state.
 */
export const SCENE_VALUES: readonly SceneName[] = [
  'welcome',
  'browse',
  'order_review',
  'close',
  'end',
];

export function isSceneName(v: unknown): v is SceneName {
  return typeof v === 'string' && (SCENE_VALUES as readonly string[]).includes(v);
}

/**
 * R19 — tolerantly resolve the driven scene name from a raw `ui.scene` message.
 *
 * SEVENTH-SESSION FIELD BUG (dev-81, "Shuchi/Delhi Decor"): the S6 review scene NEVER rendered
 * across THREE server emissions — the visitor saw plain product cards, no steppers/Confirm. The
 * `ui.scene` branch in useVoice gated ONLY on the TOP-LEVEL `m.scene`; if the propose/order_review
 * emission carried the scene name nested under `payload.scene` (or the transport wrapped it under a
 * `data` envelope), `m.scene` was `undefined`, `isSceneName` returned false, and the ENTIRE scene
 * branch was skipped — S6 could never mount. This is the exact same emission-shape ≠ subscription-
 * shape seam bug that already hit `ui.categories`, `ui.contact_field`, the end-scene payload and the
 * order-review qty defaults (all since made tolerant of top-level OR nested `payload`). This is the
 * last `ui.scene` reader that was still single-shape; make it tolerant too.
 *
 * Reads the scene from, in order: `m.scene` (frozen top-level shape, §7.1) → `m.payload.scene`
 * (nested variant) → `m.data.scene` (a transport `data` envelope). Returns the first VALID
 * SceneName, else null. Pure so the tolerance is unit-testable against captured wire fixtures.
 */
export function parseSceneName(message: unknown): SceneName | null {
  if (!message || typeof message !== 'object') return null;
  const m = message as { scene?: unknown; payload?: unknown; data?: unknown };
  if (isSceneName(m.scene)) return m.scene;
  const nestedFrom = (v: unknown): SceneName | null => {
    if (!v || typeof v !== 'object') return null;
    const s = (v as { scene?: unknown }).scene;
    return isSceneName(s) ? s : null;
  };
  return nestedFrom(m.payload) ?? nestedFrom(m.data);
}

export type SceneState = {
  /** The scene currently on stage. */
  scene: SceneName;
  /**
   * True once the visitor has locally dismissed the welcome card ("Just browsing" or a tap
   * that advances past it). Sticky: prevents a later server `ui.scene {welcome}` from
   * re-trapping a visitor who chose to browse. Reset only by a full session reset.
   */
  welcomeDismissed: boolean;
  /**
   * R12 — true once the order has been committed and we've reached the S7 'close' scene. Sticky:
   * a server-driven `ui.scene {browse}` after commit is ignored so a queued/racing post-commit
   * browse render cannot stomp the spec-sheet/close card (the field bug). Only a server
   * 'order_review' (reopen selection), local touch, or a session reset leaves 'close'.
   */
  orderClosed: boolean;
  /**
   * R13 U1 — true once the visitor has finished (the S9 'end' scene: style-snapshot reveal +
   * thanks). TERMINAL: once ended, NO scene change — server OR local — leaves 'end'. This is
   * stricter than orderClosed (which local touch could leave): 'end' is the deliberate goodbye and
   * must never revert to a product screen for the next visitor (user #3b — "Done reverted to the old
   * product screen"). Only a full session reset clears it.
   */
  sessionEnded: boolean;
};

export const INITIAL_SCENE_STATE: SceneState = {
  // R15 T1 — the resting state is now S0 attract, not the S1 contact card. The contact card is the
  // first IN-CALL beat, reached on connect via `connectScene`.
  scene: 'attract',
  welcomeDismissed: false,
  orderClosed: false,
  sessionEnded: false,
};

/**
 * R15 T1 — the connect transition: the session moves off the S0 attract resting scene into the S1
 * welcome contact card as the first in-call beat. Only fires FROM 'attract' — a reconnect/resume
 * that is already past welcome (browse/order_review/close/end) must NOT be yanked back to the card.
 * Pure so the routing (fresh-load → attract → connect → welcome; post-session → attract) is testable.
 */
export function connectScene(state: SceneState): SceneState {
  if (state.scene !== 'attract') return state;
  return { ...state, scene: 'welcome' };
}

/**
 * Apply a server-driven `ui.scene` change. Honours:
 *   - welcome-dismissed stickiness — a server return to 'welcome' after the visitor dismissed it
 *     is ignored (they stay on 'browse');
 *   - order-closed stickiness — once in 'close', a server 'browse' is ignored (F3 stomp-proof).
 *     Reaching 'close' sets orderClosed; a server 'order_review' (reopen) clears it so the visitor
 *     can edit + re-confirm.
 *   - R30 V2 — the S7 close-field GUARD (`closeFieldGuard`): while a close-card field is focused
 *     with a non-empty draft, NO server scene change leaves 'close' (the field session's glitch —
 *     a server `ui.scene:'end'` swapping the close card for the walkthrough while the visitor typed
 *     her email). This is the CLIENT-LOCAL guard: the V1 server timeout-hold makes the emission
 *     rare, this guard makes leaving 'close' mid-draft impossible. It ONLY holds 'close' — it never
 *     blocks entering 'close' or any transition off another scene, and it never overrides the
 *     visitor's own local touch (a local Done/submit still leaves, via `applyLocalScene`). Absent /
 *     false = unchanged behaviour.
 */
export function applyServerScene(
  state: SceneState,
  next: SceneName,
  closeFieldGuard = false
): SceneState {
  // R30 V2 — hold the close card while a field is focused with a draft. A server scene change AWAY
  // from 'close' is deferred (the visitor keeps typing); re-entering 'close' or an unchanged 'close'
  // still applies. The guard is the client's, regardless of what the server emits.
  if (closeFieldGuard && state.scene === 'close' && next !== 'close') {
    return state;
  }
  // R13 U1 — 'end' is terminal: once the session has ended, NO server scene change leaves it (a
  // late/queued render for the next visitor must never revert the goodbye to a product screen).
  if (state.sessionEnded) return state;
  if (next === 'welcome' && state.welcomeDismissed) {
    // Visitor already chose to browse — don't drag them back to the gate.
    return state.scene === 'welcome' ? { ...state, scene: 'browse' } : state;
  }
  // F3 stomp-proof: a late/queued server 'browse' cannot pull us out of the terminal close card.
  if (state.orderClosed && state.scene === 'close' && next === 'browse') {
    return state;
  }
  if (next === state.scene) {
    // Entering 'close'/'end' sets the sticky flag even if the value is unchanged.
    if (next === 'end' && !state.sessionEnded) return { ...state, sessionEnded: true };
    return next === 'close' && !state.orderClosed ? { ...state, orderClosed: true } : state;
  }
  return {
    ...state,
    scene: next,
    orderClosed: next === 'close' ? true : state.orderClosed,
    sessionEnded: next === 'end' ? true : state.sessionEnded,
  };
}

/**
 * Apply a local touch-driven scene change (always allowed — touch is a full peer to voice).
 * Advancing away from 'welcome' marks it dismissed so the server can't re-open it. Reaching
 * 'close' locally (e.g. the confirm→close flow) sets orderClosed.
 */
export function applyLocalScene(state: SceneState, next: SceneName): SceneState {
  // R13 U1 — once ended, even local touch cannot leave 'end' (the deliberate goodbye is terminal;
  // the "start a new conversation" affordance uses a full session reset, not a scene change).
  if (state.sessionEnded) return state;
  const welcomeDismissed =
    state.welcomeDismissed || (state.scene === 'welcome' && next !== 'welcome');
  const orderClosed = state.orderClosed || next === 'close';
  const sessionEnded = state.sessionEnded || next === 'end';
  if (
    next === state.scene &&
    welcomeDismissed === state.welcomeDismissed &&
    orderClosed === state.orderClosed &&
    sessionEnded === state.sessionEnded
  )
    return state;
  return { scene: next, welcomeDismissed, orderClosed, sessionEnded };
}

/** Explicit "Just browsing" skip from the welcome card. */
export function dismissWelcome(state: SceneState): SceneState {
  return applyLocalScene(state, 'browse');
}
